<template>
  <form class="form" @submit.prevent="onSearch">
    <div class="search-box">
      <input
        class="form-control"
        :class="{ warning: isInvalidInternetNumber }"
        :placeholder="`${$t('searchBox.searchHint')}`"
        type="text"
        v-model.trim="searchQuery"
      />
      <button
        class="search-clear"
        v-show="searchQuery"
        type="button"
        @click.prevent="clearQuery()"
      >
        <fa icon="times-circle"></fa>
      </button>
      <button
        class="search-submit"
        type="button"
        @click="onSearch"
        :value="$t('searchBox.search')"
      >
        <fa icon="search"></fa>
      </button>
      <label
        v-if="networkInfo && !isInvalidInternetNumber"
        class="resource-example"
      >
        <small>
          <i>{{ $t('searchBox.eg', 1) }}:</i>
          <router-link tag="a" :to="'/search/AS' + networkInfo.asn">
            AS{{ networkInfo.asn }}
          </router-link>
          {{ $t('searchBox.or') }}
          <router-link tag="a" :to="'/search/' + networkInfo.prefix">
            {{ networkInfo.prefix }}
          </router-link>
        </small>
      </label>
    </div>
    <div v-if="isInvalidInternetNumber" class="invalid-input-warning">
      {{ $t('searchBox.warningText') }}
    </div>
  </form>
</template>

<script>
import { mapState } from 'vuex';
import { isNavigationFailure, NavigationFailureType } from 'vue-router';
import {
  INTERNET_NUMBER_RESOURCE_TYPE,
  validateInternetNumberType,
  normalizeInternetNumber,
  checkIsSameResourceType,
} from '../utils/validateInternetNumberResource';

export default {
  name: 'SearchBoxInHeader',
  data() {
    return {
      searchQuery: '',
      isInvalidInternetNumber: false,
      activeTab: 'apnic-at-a-glance',
    };
  },
  computed: {
    ...mapState(['query']),

    networkInfo() {
      return this.$store.state.networkInfo;
    },
  },
  props: {
    showInfo: { type: Boolean, default: true },
  },
  watch: {
    searchQuery() {
      if (this.searchQuery === '') this.isInvalidInternetNumber = false;
    },
    query: {
      immediate: true,
      handler(val) {
        // Avoid update this.searchQuery when query is null or including "%",
        // e.g. query might be 220.233.36.0%2F22, and will be formatted as 220.233.36.0/22.
        if (val && !val.includes('%')) {
          this.searchQuery = val;
        }
      },
    },
    $route: {
      immediate: true,
      handler(to) {
        this.activeTab = to && to.name ? to.name : 'apnic-at-a-glance';
      },
    },
  },
  mounted() {
    const width = window.innerWidth;
    if (width > 768) this.$store.commit('setShowSideBar', true);
  },
  methods: {
    clearQuery() {
      this.searchQuery = '';
    },
    onSearch() {
      if (!this.searchQuery) {
        return;
      }

      const targetResourceType = validateInternetNumberType(this.searchQuery);

      if (targetResourceType === INTERNET_NUMBER_RESOURCE_TYPE.INVALID) {
        this.isInvalidInternetNumber = true;
        return;
      } else {
        this.isInvalidInternetNumber = false;

        this.searchQuery = normalizeInternetNumber(this.searchQuery);

        const isOnSingleWidgetPage = this.$route.name === 'singleWidget';

        if (isOnSingleWidgetPage) {
          const currentResourceType = validateInternetNumberType(this.query);
          const isSameResourceType = checkIsSameResourceType(
            currentResourceType,
            targetResourceType
          );

          if (isSameResourceType) {
            // Only update hash if new resource is a same type as current one.
            // So users will stay on the current single widget page.
            this.$router.push({ hash: `w.resource=${this.searchQuery}` });

            // Updating hash only won't trigger router event, so window.location.reload() is necessary.
            window.location.reload();
          } else {
            // Navigate users to "At a glance" tab if new resource is a different type.
            this.navigateToSpecificTab('apnic-at-a-glance');
          }
        } else {
          // Users will stay on current tab if it's on a tab page rather than single widget.
          this.navigateToSpecificTab(this.activeTab);
        }
      }
    },
    navigateToSpecificTab(tab) {
      this.$router
        .push(`/search/${this.searchQuery}#tab=${tab}`)
        .catch((err) => {
          const isRedirectFailure = isNavigationFailure(
            err,
            NavigationFailureType.redirected
          );

          if (!isRedirectFailure) {
            console.error('Error: ', err);
          }
        });
    },
  },
  created() {
    this.$store.dispatch('getNetworkInfo');
  },
};
</script>

<style scoped lang="scss">
.form {
  width: 100%;
  height: 100%;

  .example {
    margin-top: 4px;
  }
}

.search-box {
  position: relative;
  height: 100%;

  .form-control {
    display: inline-block;
    font-size: 16px;
    padding-right: 30px;
    height: 100%;
    width: 19rem;

    &::placeholder {
      font-size: 0.9rem;
    }
  }

  .warning {
    color: $warning-red;
    border-color: $warning-red;
    box-shadow: none;
  }

  svg[data-icon='search'] {
    color: $text-black;
    font-size: 18px;
  }

  .search-clear {
    color: #999;
    background: transparent;
    border-color: transparent;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 14.5rem;
  }
  .text-secondary {
    margin-top: 4px;
  }
  .search-submit {
    color: #999;
    background: transparent;
    border-color: transparent;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 16.5rem;
  }

  .resource-example {
    padding-left: 1rem;

    @media screen and (max-width: 1200px) {
      display: none;
    }
  }
}

.invalid-input-warning {
  margin-top: 6px;
  margin-bottom: 8px;
  font-size: 0.75rem;
  color: $warning-red;
}
</style>
